import {createSelector} from 'reselect'
import isEqual from 'lodash/isEqual.js'

import api from '../../../common/api.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {presetOptionsSelector, setPreset} from '../../../data/presets.js'
import {formsSelector} from '../../../redux/selectors/ui/forms.js'
import {
  getState,
  onlyIfForm,
  removeForm,
  setForm,
  updateForm,
} from '../../../store.js'
import {
  DragAndDropList,
  Draggable,
  DragHandle,
  MOVE_AFTER,
  MOVE_BEFORE,
} from '../../../common/components/DragAndDrop.js'
import Icon from '../../../common/icons/Icon.js'
import {buildPath} from '../../../common/querystring.js'
import handleListSelection from '../../../common/handleListSelection.js'
import {useMemo} from 'react'
import Checkbox from '../../../common/components/Checkbox.js'
import html from '../../../common/html.js'
import {plural} from '../../../common/components/Plural.js'
import ButtonLink from '../../../common/components/Button/ButtonLink.js'

export const MODAL_FORM = 'preset_sort_modal'

export function showPresetSortModal() {
  const listOptions = presetOptionsSelector(getState())

  setForm(MODAL_FORM, {
    selectedIDs: [],
    listOptions,
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export const modalFormSelector = createSelector(
  formsSelector,
  (forms) => forms[MODAL_FORM],
)

async function onPresetMove(values, nearValue, where) {
  const {listOptions: originalListOptions} = modalFormSelector(getState())

  // get the options that moved and the rest in different lists
  const [options, listOptions] = originalListOptions.reduce(
    (prev, option) => {
      if (values.includes(option.value)) {
        prev[0].push(option)
      } else {
        prev[1].push(option)
      }

      return prev
    },
    [[], []],
  )

  // get index of near item
  const nearIndex = listOptions.findIndex(
    (option) => option.value === nearValue,
  )

  // put moving item in it's place
  if (where === MOVE_BEFORE) {
    listOptions.splice(nearIndex, 0, ...options)
  } else if (where === MOVE_AFTER) {
    listOptions.splice(nearIndex + 1, 0, ...options)
  }

  if (!isEqual(listOptions, originalListOptions)) {
    updateModalForm({listOptions})
  }
}

function moveToTop() {
  const {selectedIDs, listOptions} = modalFormSelector(getState())

  const firstOption = listOptions.filter(
    (option) => !selectedIDs.includes(option.value),
  )[0]

  if (firstOption) {
    onPresetMove(selectedIDs, firstOption.value, MOVE_BEFORE)
  }
}

function moveToBottom() {
  const {selectedIDs, listOptions} = modalFormSelector(getState())

  const list = listOptions.filter(
    (option) => !selectedIDs.includes(option.value),
  )
  const lastOption = list[list.length - 1]

  if (lastOption) {
    onPresetMove(selectedIDs, lastOption.value, MOVE_AFTER)
  }
}

function onDragStart(presetID) {
  const {selectedIDs} = modalFormSelector(getState())

  const movingSelected = selectedIDs.includes(presetID)
  const count = movingSelected ? selectedIDs.length : 1

  return {
    values: movingSelected ? selectedIDs : [presetID],
    dragElementFunc() {
      return movingSelected
        ? html`<div class="drag-summary">
            <strong
              >Moving
              ${plural(count)`${count} selected presets${['s']}`}</strong
            >
          </div>`
        : null
    },
  }
}

export async function savePresetSort() {
  try {
    const {listOptions} = modalFormSelector(getState())

    updateModalForm({isSaving: true, serverError: null})

    const presets = await Promise.all(
      listOptions.map(async (option, index) => {
        const {json} = await api.put(buildPath(['preset', option.value]), {
          index,
        })

        return json
      }),
    )

    for (const preset of presets) {
      setPreset(preset)
    }

    closeModal()

    showMessageToast('Preset Display Order Updated')
  } catch (err) {
    updateModalForm({
      serverError: err.message || err.error_message,
      isSaving: false,
    })
  }
}

function PresetSortModal({form}) {
  const list = useMemo(
    () => form.listOptions.map((option) => option.value),
    [form.listOptions],
  )
  const areAllItemsSelected =
    list.length > 0 && list.length === form.selectedIDs.length
  const areIndeterminateItemsSelected =
    !areAllItemsSelected && form.selectedIDs.length > 0

  return (
    <ConfirmModal
      title="Arrange Preset Order"
      modalSize="sm"
      onConfirm={() => savePresetSort()}
      onCancel={() => closeModal()}
      confirmText="Save"
      cancelText="Cancel"
      isSaving={form.isSaving}
      error={form.serverError}
    >
      <DragAndDropList onMove={onPresetMove} onDragStart={onDragStart}>
        <p className="fs-01 lh-md margin-bottom-15">
          <strong className="inline-block v-align-middle">
            Reorder your presets by dragging the{' '}
            <span className="inline-block v-align-middle op-30">
              <Icon
                name="drag-handle"
                className="i-drag-handle block fs-03-04 lh-sm"
              />
            </span>{' '}
            icon.
          </strong>
        </p>
        <p className="fs-01 lh-md margin-bottom-25">
          Use the checkboxes to select multiple presets and reorder them as a
          group.
        </p>
        <div className="margin-bottom-15">
          <Checkbox
            id="preset-sort-select-all"
            label={areAllItemsSelected ? 'Deselect all' : 'Select all'}
            className="margin-bottom-0"
            checked={areAllItemsSelected}
            indeterminate={areIndeterminateItemsSelected}
            onClick={(checked) =>
              updateModalForm({
                selectedIDs: checked ? list : [],
              })
            }
          />
        </div>
        <div className="relative flex">
          <div className="wrap--reposition-btns-outer wrap--reposition-btns-in-modal">
            {form.selectedIDs.length > 0 && (
              <div className="wrap--reposition-btns">
                <div className="margin-bottom-1">
                  <ButtonLink
                    className="btn--reposition-top no-underline"
                    onClick={() => moveToTop()}
                    title="Move selected items to top"
                  >
                    <strong>↑</strong>
                  </ButtonLink>
                </div>
                <div>
                  <ButtonLink
                    className="btn--reposition-bottom no-underline"
                    onClick={() => moveToBottom()}
                    title="Move selected items to bottom"
                  >
                    <strong>↓</strong>
                  </ButtonLink>
                </div>
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="wrap--modal-draggable-items">
              {form.listOptions.map((option, index) => (
                <Draggable
                  className="flex wrap--draggable-item border-top--light align-items-center"
                  key={option.value}
                  id={option.value}
                  uiIndex={index}
                >
                  <DragHandle className="padding-top-10 padding-bottom-10 " />
                  <Checkbox
                    className="margin-bottom-0 margin-right-7"
                    checked={form.selectedIDs.includes(option.value)}
                    onClick={(checked, event) =>
                      updateModalForm({
                        selectedIDs: handleListSelection({
                          value: option.value,
                          isSelected: checked,
                          isShiftKey: event.shiftKey,
                          selected: form.selectedIDs,
                          list,
                        }),
                      })
                    }
                  />
                  <div className="fs-01 flex--justify-col padding-top-10 padding-bottom-10 flex-grow">
                    <span className="label--selectable__text fs-00 lh-md unbold flex align-items-center">
                      {option.display}
                    </span>
                  </div>
                </Draggable>
              ))}
            </div>
          </div>
        </div>
      </DragAndDropList>
    </ConfirmModal>
  )
}

export default onlyIfForm(PresetSortModal, modalFormSelector)
